import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  TextField, IconButton, Tooltip, Box, Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import SettingsIcon from '@material-ui/icons/Settings';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import BusinessIcon from '@material-ui/icons/Business';
import RoomIcon from '@material-ui/icons/Room';
import axios from 'axios';
import SetupStatusBadge from './SetupStatusBadge';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 20px',
    background: '#ffffff',
    borderBottom: '1px solid #e0e0e0',
    gap: 16,
    minHeight: 60,
    flexShrink: 0,
  },
  pickers: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    minWidth: 0,
    maxWidth: 720,
  },
  picker: {
    flex: 1,
    minWidth: 0,
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fafafa',
      fontSize: '0.85rem',
    },
    '& .MuiOutlinedInput-input': {
      padding: '8px 8px',
    },
  },
  rightSide: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexShrink: 0,
  },
  settingsBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    background: '#f5f5f5',
    '&:hover': { background: '#eeeeee' },
  },
  optionRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  optionName: {
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#333',
  },
  optionMeta: {
    fontSize: '0.7rem',
    color: '#999',
  },
  iconAdornment: {
    color: '#999',
    marginLeft: 4,
    fontSize: 18,
  },
}));

/**
 * Header with horizontal Client + Jobsite autocompletes + setup badge + settings button
 */
const SiteTakeoffHeader = ({
  url,
  clients = [],
  selectedClient,
  selectedJobsite,
  onClientChange,
  onJobsiteChange,
  setupComplete,
  onSettingsClick,
  onGuideClick,
}) => {
  const classes = useStyles();

  // ===== Client search (filter Redux clients list) =====
  const [clientQuery, setClientQuery] = useState('');

  const clientOptions = useMemo(() => {
    if (!clients?.length) return [];
    if (!clientQuery || clientQuery.trim().length < 2) return [];
    const q = clientQuery.toLowerCase();
    return clients.filter(c => {
      const name = (c.displayName || c.searchName || c.companyName || c.name || '').toLowerCase();
      return name.includes(q);
    }).slice(0, 30);
  }, [clients, clientQuery]);

  // ===== Jobsite search =====
  const [jobsiteOptions, setJobsiteOptions] = useState([]);
  const [jobsiteLoading, setJobsiteLoading] = useState(false);
  const [jobsiteQuery, setJobsiteQuery] = useState('');

  useEffect(() => {
    if (!url) return;
    // Allow empty query when a client is selected (show all their sites)
    if (!selectedClient && jobsiteQuery.trim().length < 1) {
      setJobsiteOptions([]);
      return;
    }
    let cancelled = false;
    setJobsiteLoading(true);
    const timer = setTimeout(async () => {
      try {
        const params = { q: jobsiteQuery };
        if (selectedClient?._id) params.client = selectedClient._id;
        const resp = await axios.get(`${url}/api/customer/jobsites/search`, { params });
        if (!cancelled) {
          setJobsiteOptions(resp.data?.jobsites || []);
        }
      } catch (err) {
        if (!cancelled) setJobsiteOptions([]);
      } finally {
        if (!cancelled) setJobsiteLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [jobsiteQuery, selectedClient, url]);

  return (
    <div className={classes.header}>
      <div className={classes.pickers}>
        <div data-tour="site-takeoff-client-picker" style={{ flex: 1, minWidth: 0 }}>
          <Autocomplete
            className={classes.picker}
            size="small"
            options={clientOptions}
            value={selectedClient || null}
            onChange={(e, val) => onClientChange(val)}
            onInputChange={(e, val) => setClientQuery(val)}
            getOptionLabel={(opt) => opt?.displayName || opt?.searchName || opt?.companyName || opt?.name || ''}
            getOptionSelected={(opt, val) => opt?._id === val?._id}
            filterOptions={(x) => x}
            renderOption={(opt) => (
              <div className={classes.optionRow}>
                <span className={classes.optionName}>{opt.displayName || opt.searchName || opt.companyName || opt.name}</span>
                {opt.address && <span className={classes.optionMeta}>{opt.address}</span>}
              </div>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search client..."
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <BusinessIcon className={classes.iconAdornment} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </div>

        <div data-tour="site-takeoff-jobsite-picker" style={{ flex: 1, minWidth: 0 }}>
          <Autocomplete
            className={classes.picker}
            size="small"
            options={jobsiteOptions}
            loading={jobsiteLoading}
            value={selectedJobsite || null}
            onChange={(e, val) => onJobsiteChange(val)}
            onInputChange={(e, val) => setJobsiteQuery(val)}
            getOptionLabel={(opt) => {
              if (!opt) return '';
              const name = opt.name || '';
              const addr = opt.address || '';
              return name && addr ? `${name} — ${addr}` : (name || addr);
            }}
            getOptionSelected={(opt, val) => opt?._id === val?._id}
            filterOptions={(x) => x}
            renderOption={(opt) => (
              <div className={classes.optionRow}>
                <span className={classes.optionName}>{opt.name || 'Unnamed'}</span>
                <span className={classes.optionMeta}>
                  {[opt.address, opt.city, opt.state].filter(Boolean).join(', ')}
                  {opt.client?.companyName ? ` · ${opt.client.companyName}` : ''}
                </span>
              </div>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedClient ? 'Select jobsite' : 'Search any jobsite...'}
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <RoomIcon className={classes.iconAdornment} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </div>
      </div>

      <div className={classes.rightSide}>
        {selectedJobsite && (
          <div data-tour="site-takeoff-setup-status">
            <SetupStatusBadge complete={setupComplete} />
          </div>
        )}
        <Tooltip title="Show guide">
          <IconButton className={classes.settingsBtn} onClick={onGuideClick} size="small">
            <HelpOutlineIcon style={{ fontSize: 20, color: '#666' }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Property settings">
          <IconButton
            className={classes.settingsBtn}
            onClick={onSettingsClick}
            size="small"
            data-tour="site-takeoff-settings-button"
          >
            <SettingsIcon style={{ fontSize: 20, color: '#666' }} />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
};

export default SiteTakeoffHeader;
